import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, PlayCircle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  countLessons,
  formatCurrencyFromCents,
  formatDuration,
  totalDurationSeconds,
} from '../utils/format';
import { groupFeedApi } from '../feedApi';

const EMPTY_ARR = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});

function firstLesson(modules) {
  for (const m of modules || []) {
    const lessons = Array.isArray(m?.lessons)
      ? m.lessons
      : Array.isArray(m?.videos)
      ? m.videos
      : Array.isArray(m?.items)
      ? m.items
      : [];
    if (lessons.length) return lessons[0];
  }
  return null;
}

function lessonVideoUrl(lesson) {
  return (
    lesson?.video_url ||
    lesson?.url ||
    lesson?.signed_url ||
    lesson?.playback_url ||
    ''
  );
}

export default function CourseViewerDialog({
  open,
  onOpenChange,
  groupId,
  courseId,
  item, // optional fallback for title/description if builder fails
  initialAccess = null,
  initialBuilder = null,
  onCheckout,
}) {
  const { t } = useTranslation();

  const [access, setAccess] = useState(initialAccess);
  const [builder, setBuilder] = useState(initialBuilder);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAccess(initialAccess || null);
  }, [initialAccess]);

  useEffect(() => {
    setBuilder(initialBuilder || null);
  }, [initialBuilder]);

  useEffect(() => {
    if (!open) return;
    if (!groupId || !courseId) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        // 1) Access for lock + price
        try {
          const a = await groupFeedApi.getCourseAccess(groupId, courseId);
          if (!cancelled) setAccess(a);
        } catch (e) {
          // ignore
        }

        // 2) Builder for modules/lessons
        const b = await groupFeedApi.getCourseBuilder(groupId, courseId);
        if (!cancelled) setBuilder(b);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.detail ||
            t('feed.course.loadError', { defaultValue: 'Failed to load course content' })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, groupId, courseId, t]);

  // Fix for Netlify lint: stabilize course/modules identities
  const course = useMemo(() => {
    return builder?.course || item?.course_preview || EMPTY_OBJ;
  }, [builder?.course, item?.course_preview]);

  const modules = useMemo(() => {
    return Array.isArray(builder?.modules) ? builder.modules : EMPTY_ARR;
  }, [builder?.modules]);

  const title =
    course?.title ||
    item?.title ||
    t('feed.course.title', { defaultValue: 'Course' });

  const description =
    course?.description ||
    item?.body ||
    item?.description ||
    '';

  const currency = String(
    access?.currency ??
      course?.currency ??
      item?.course_preview?.currency ??
      'usd'
  ).toUpperCase();

  const priceCents = Number(
    access?.price_cents ??
      course?.price_cents ??
      item?.course_preview?.price_cents ??
      0
  ) || 0;

  const isFree = priceCents <= 0;
  const isUnlocked = isFree || access?.has_access === true;

  const moduleCount = useMemo(() => {
    const fromCourse = Number(course?.module_count ?? NaN);
    if (Number.isFinite(fromCourse)) return fromCourse;
    return modules.length;
  }, [course, modules]);

  const lessonCount = useMemo(() => {
    const fromCourse = Number(course?.lesson_count ?? NaN);
    if (Number.isFinite(fromCourse)) return fromCourse;
    return countLessons(modules);
  }, [course, modules]);

  const status = String(course?.status || item?.course_preview?.status || 'draft');

  const totalSeconds = useMemo(() => totalDurationSeconds(modules), [modules]);

  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    const first = firstLesson(modules);
    setSelected(first);

    const keys = (modules || []).map((m, idx) => m?.id || `m_${idx}`);
    setExpanded(new Set(keys));
  }, [open, courseId, modules]);

  const toggleModule = (moduleKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(moduleKey)) next.delete(moduleKey);
      else next.add(moduleKey);
      return next;
    });
  };

  const handleSelectLesson = (lesson) => {
    const isPreview = !!lesson?.is_preview;
    if (!isUnlocked && !isPreview) {
      toast.error(
        t('feed.course.locked', { defaultValue: 'This course is locked until you purchase it.' })
      );
      return;
    }
    setSelected(lesson);
  };

  const handleCheckout = async () => {
    if (isUnlocked) return;
    await onCheckout?.();
  };

  const showLocked = !isUnlocked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-xl">{title}</DialogTitle>
              <DialogDescription className="mt-1">{description}</DialogDescription>

              <div className="mt-2 text-xs text-slate-600">
                {moduleCount} {t('feed.course.modules', { defaultValue: 'modules' })} - {lessonCount}{' '}
                {t('feed.course.lessons', { defaultValue: 'lessons' })} - {status}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {isFree ? (
                  <Badge variant="outline">{t('feed.course.free', { defaultValue: 'Free' })}</Badge>
                ) : (
                  <Badge variant="outline">{formatCurrencyFromCents(priceCents, currency)}</Badge>
                )}

                <Badge variant="outline">
                  {moduleCount} {t('feed.course.modules', { defaultValue: 'modules' })}
                </Badge>

                <Badge variant="outline">
                  {lessonCount} {t('feed.course.lessons', { defaultValue: 'lessons' })}
                </Badge>

                <Badge variant="secondary">{status}</Badge>

                {totalSeconds > 0 && (
                  <Badge variant="outline">{formatDuration(totalSeconds)}</Badge>
                )}

                {showLocked && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <Lock size={12} />
                    {t('feed.course.lockedBadge', { defaultValue: 'Locked' })}
                  </Badge>
                )}

                {loading && (
                  <Badge variant="outline">{t('feed.course.loading', { defaultValue: 'Loading...' })}</Badge>
                )}
              </div>
            </div>

            {showLocked && (
              <Button onClick={handleCheckout} className="shrink-0">
                <Lock size={16} className="mr-2" />
                {t('feed.course.buy', { defaultValue: 'Buy to unlock' })}
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-[320px,1fr] gap-4 mt-2">
          {/* Modules / lessons */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b bg-slate-50 text-sm font-medium">
              {t('feed.course.curriculum', { defaultValue: 'Curriculum' })}
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {modules.length === 0 ? (
                <div className="p-3 text-sm text-slate-500">
                  {t('feed.course.noModules', { defaultValue: 'No modules yet.' })}
                </div>
              ) : (
                modules.map((m, idx) => {
                  const moduleKey = m?.id || `m_${idx}`;
                  const lessons = Array.isArray(m?.lessons)
                    ? m.lessons
                    : Array.isArray(m?.videos)
                    ? m.videos
                    : Array.isArray(m?.items)
                    ? m.items
                    : [];

                  const isOpen = expanded.has(moduleKey);

                  return (
                    <div key={moduleKey} className="border-b last:border-b-0">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
                        onClick={() => toggleModule(moduleKey)}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {m?.title || m?.name || `Module ${idx + 1}`}
                          </p>
                          <p className="text-xs text-slate-500">
                            {lessons.length} {t('feed.course.lessons', { defaultValue: 'lessons' })}
                          </p>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {isOpen && (
                        <div className="pb-2">
                          {lessons.map((l, li) => {
                            const lessonKey = l?.id || `${moduleKey}_l_${li}`;
                            const isSelected =
                              (selected?.id && selected.id === l?.id) ||
                              (!selected?.id && selected?.title === l?.title);

                            const isPreview = !!l?.is_preview;

                            return (
                              <button
                                key={lessonKey}
                                type="button"
                                onClick={() => handleSelectLesson(l)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                                  isSelected ? 'bg-slate-50' : ''
                                } ${showLocked && !isPreview ? 'opacity-70' : ''}`}
                              >
                                <PlayCircle size={16} className="shrink-0 text-slate-500" />
                                <span className="truncate flex-1">
                                  {l?.title || l?.name || `Lesson ${li + 1}`}
                                </span>
                                {isPreview && (
                                  <Badge variant="outline" className="text-[10px] py-0 px-1">
                                    Preview
                                  </Badge>
                                )}
                                {showLocked && !isPreview && (
                                  <Lock size={14} className="shrink-0 text-slate-500" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Player */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b bg-slate-50 text-sm font-medium flex items-center justify-between gap-3">
              <span className="truncate">
                {selected?.title || selected?.name || t('feed.course.selectLesson', { defaultValue: 'Select a lesson' })}
              </span>
              {showLocked && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Lock size={12} />
                  {t('feed.course.lockedHint', { defaultValue: 'Purchase required' })}
                </span>
              )}
            </div>

            <div className="p-3">
              {showLocked && !selected?.is_preview ? (
                <div className="aspect-video w-full rounded-lg border bg-slate-50 flex items-center justify-center">
                  <div className="text-center">
                    <Lock className="mx-auto mb-2 text-slate-500" />
                    <p className="text-sm font-medium">
                      {t('feed.course.lockedTitle', { defaultValue: 'This course is locked' })}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {t('feed.course.lockedSubtitle', { defaultValue: 'Buy the course to watch lessons.' })}
                    </p>
                    <Button className="mt-3" onClick={handleCheckout}>
                      {t('feed.course.buy', { defaultValue: 'Buy to unlock' })}
                    </Button>
                  </div>
                </div>
              ) : lessonVideoUrl(selected) ? (
                <video
                  key={lessonVideoUrl(selected)}
                  src={lessonVideoUrl(selected)}
                  controls
                  className="w-full rounded-lg border"
                />
              ) : (
                <div className="aspect-video w-full rounded-lg border bg-slate-50 flex items-center justify-center text-sm text-slate-500">
                  {t('feed.course.noVideo', { defaultValue: 'No video URL for this lesson yet.' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
